import { RadioGroup as CompassRadioGroup } from "@circleco/compass/components/RadioGroup";
import { Controller, useFormContext } from "react-hook-form";

import {
  RadioGroup as UIKitRadioGroup,
  type RadioGroupProps,
} from "@/react/components/shared/uikit/Form/RadioGroup";
import type { RadioGroupRadioProps } from "@/react/components/shared/uikit/Form/RadioGroup/Radio";
import { useIsCircleV4FeatureEnabled } from "@/react/hooks/useIsCircleV4FeatureEnabled";

const hasUnsupportedOptionFeatures = (
  options: RadioGroupRadioProps["options"],
) =>
  options.some(
    option =>
      Boolean(option.icon) ||
      Boolean(option.info) ||
      Boolean(option.infoSubText),
  );

export const RadioGroup = ({ variant = "box", ...rest }: RadioGroupProps) => {
  const { control } = useFormContext();
  const isCircleV4 = useIsCircleV4FeatureEnabled(
    "circle_v4_form_elements_enabled",
  );

  if (!isCircleV4 || variant !== "radio") {
    return <UIKitRadioGroup variant={variant} {...rest} />;
  }

  const radioProps: RadioGroupRadioProps = rest as RadioGroupRadioProps;

  const {
    name,
    rules,
    options,
    renderOption,
    disabled: isDisabled = false,
  } = radioProps;

  if (renderOption || hasUnsupportedOptionFeatures(options)) {
    return <UIKitRadioGroup variant={variant} {...rest} />;
  }

  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      render={({ field: { onChange, value } }) => (
        <CompassRadioGroup
          legend=""
          value={typeof value === "string" ? value : undefined}
          onValueChange={selectedValue => onChange(selectedValue)}
          options={options.map(option => ({
            label: option.label,
            value: option.value,
            description: option.description,
            disabled: isDisabled,
          }))}
        />
      )}
    />
  );
};

RadioGroup.displayName = "Form.RadioGroup";
